import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Save, Pencil, Check, X } from "lucide-react";
import {
  listPillars,
  createPillar,
  updatePillar,
  deletePillar,
  suggestPillars,
} from "@/lib/weave";

const COLORS = [
  ["teal", "var(--teal)"],
  ["yellow", "var(--yellow-deep)"],
  ["red", "var(--red)"],
  ["ink", "var(--ink)"],
];

function PillarCard({ pillar, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...pillar });

  useEffect(() => setDraft({ ...pillar }), [pillar]);

  const colorVal = COLORS.find(([k]) => k === pillar.color)?.[1] || "var(--teal)";

  if (!editing) {
    return (
      <div className="card-tech rounded-sm p-5 flex flex-col gap-3" data-testid={`pillar-${pillar.pillar_id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorVal }} />
            <h3 className="font-display text-xl tracking-tight">{pillar.name}</h3>
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 hover:bg-[var(--bg-2)] rounded-sm"
              data-testid={`pillar-edit-${pillar.pillar_id}`}
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={() => onDelete(pillar.pillar_id)}
              className="p-1.5 hover:bg-[var(--bg-2)] rounded-sm text-[var(--red)]"
              data-testid={`pillar-delete-${pillar.pillar_id}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        {pillar.description && (
          <p className="text-sm text-[var(--ink-2)]">{pillar.description}</p>
        )}
        {Array.isArray(pillar.keywords) && pillar.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {pillar.keywords.map((k, i) => (
              <Badge
                key={i}
                variant="outline"
                className="rounded-sm border-[var(--line)] font-mono-mark text-[10px]"
              >
                {k}
              </Badge>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card-tech rounded-sm p-5 space-y-3">
      <Input
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        className="rounded-sm border-[var(--line)] h-10 text-base font-display tracking-tight"
        placeholder="Pillar name"
      />
      <Textarea
        rows={3}
        value={draft.description}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        placeholder="One-line description of this pillar."
        className="rounded-sm border-[var(--line)]"
      />
      <Input
        value={(draft.keywords || []).join(", ")}
        onChange={(e) =>
          setDraft({
            ...draft,
            keywords: e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          })
        }
        placeholder="comma, separated, keywords"
        className="rounded-sm border-[var(--line)] h-10"
      />
      <div className="flex gap-2">
        {COLORS.map(([k, c]) => (
          <button
            key={k}
            type="button"
            onClick={() => setDraft({ ...draft, color: k })}
            className={`w-7 h-7 rounded-full border-2 ${
              draft.color === k ? "border-[var(--ink)]" : "border-transparent"
            }`}
            style={{ background: c }}
            aria-label={k}
          />
        ))}
      </div>
      <div className="flex gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => {
            setEditing(false);
            setDraft({ ...pillar });
          }}
        >
          <X size={14} className="mr-1" /> Cancel
        </Button>
        <Button
          size="sm"
          className="btn-primary rounded-full"
          onClick={async () => {
            await onUpdate(pillar.pillar_id, draft);
            setEditing(false);
          }}
          data-testid={`pillar-save-${pillar.pillar_id}`}
        >
          <Check size={14} className="mr-1" /> Save
        </Button>
      </div>
    </div>
  );
}

export default function Pillars() {
  const [pillars, setPillars] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const load = () => listPillars().then(setPillars).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!name.trim()) return;
    setAdding(true);
    try {
      await createPillar({
        name: name.trim(),
        description: desc.trim(),
        keywords: [],
        color: "teal",
      });
      setName("");
      setDesc("");
      toast.success("Pillar added.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add.");
    } finally {
      setAdding(false);
    }
  };

  const update = async (id, draft) => {
    try {
      await updatePillar(id, {
        name: draft.name,
        description: draft.description,
        keywords: draft.keywords || [],
        color: draft.color || "teal",
      });
      toast.success("Updated.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pillar?")) return;
    try {
      await deletePillar(id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const askCoach = async () => {
    setThinking(true);
    try {
      const res = await suggestPillars();
      const suggestions = res.pillars || [];
      if (!suggestions.length) {
        toast.message("Set up your foundations first to get pillar suggestions.");
        return;
      }
      // create them all
      for (const s of suggestions) {
        await createPillar({
          name: s.name,
          description: s.description || "",
          keywords: s.keywords || [],
          color: ["teal", "yellow", "red", "ink"][suggestions.indexOf(s) % 4],
        });
      }
      toast.success(`Added ${suggestions.length} pillars from your foundations.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach is unavailable.");
    } finally {
      setThinking(false);
    }
  };

  return (
    <div data-testid="pillars-page" className="space-y-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Module 06</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Content pillars.
          </h1>
          <p className="mt-3 text-[var(--ink-2)] max-w-2xl">
            3–4 themes you'd happily talk about for a year. A mix of <span className="text-[var(--teal)]">expertise</span> and <span className="text-[var(--red)]">your story</span>.
          </p>
        </div>
        <Button
          onClick={askCoach}
          disabled={thinking}
          className="btn-accent rounded-full h-11 px-5 flex items-center gap-2"
          data-testid="pillars-suggest-btn"
        >
          <Sparkles size={16} /> {thinking ? "Thinking…" : "Suggest from foundations"}
        </Button>
      </div>

      {/* add new */}
      <div className="card-tech rounded-sm p-5 space-y-3">
        <div className="overline">Add a pillar</div>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Building in public"
          className="rounded-sm border-[var(--line)] h-11"
          data-testid="pillar-name-input"
        />
        <Textarea
          rows={2}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="A one-line description of what this pillar covers."
          className="rounded-sm border-[var(--line)]"
        />
        <div className="flex justify-end">
          <Button
            onClick={add}
            disabled={adding}
            className="btn-primary rounded-full h-10 px-5"
            data-testid="pillar-add-btn"
          >
            <Plus size={14} className="mr-1" /> Add pillar
          </Button>
        </div>
      </div>

      {pillars.length === 0 ? (
        <div className="card-tech rounded-sm p-10 text-center">
          <div className="overline mb-2">No pillars yet</div>
          <p className="text-[var(--ink-2)]">Add 3–4 themes, or let the coach suggest some.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pillars.map((p) => (
            <PillarCard key={p.pillar_id} pillar={p} onUpdate={update} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}
